import React, { useEffect, useState } from "react";
import { CreditCard, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { planLabel } from "@/lib/subscription";

// Settings › Subscription card. Shows the caller's latest plan and offers a
// no-refund cancel: access continues until the end of the billing cycle.
export const SubscriptionCard = () => {
  const { refresh } = useAuth();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/billing/subscription");
      setSub(r.data);
    } catch {}
  };
  useEffect(() => {
    load();
  }, []);

  if (!sub || !sub.plan) return null;

  const endDate = sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : null;
  const price = sub.amount_cents != null ? `$${(sub.amount_cents / 100).toFixed(2)}` : null;
  const isCancelled = sub.status === "cancelled";

  const cancel = async () => {
    const msg =
      `Cancel your ${sub.trial ? "free trial" : "subscription"}?\n\n` +
      (sub.trial
        ? `Your card will NOT be charged and you'll keep access until ${endDate}.`
        : `You'll keep full access until ${endDate}, then your subscription ends. Your card won't be charged again.`) +
      `\n\nNo refunds are issued for cancellations.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.post("/billing/cancel");
      const until = r?.data?.access_until
        ? new Date(r.data.access_until).toLocaleDateString()
        : endDate;
      toast.success(`Cancelled — access continues until ${until}. No further charges.`);
      await Promise.all([refresh(), load()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="obs-card" data-testid="settings-subscription-card">
      <div className="flex items-center gap-3 mb-4">
        <CreditCard className="h-5 w-5 text-blaze" />
        <div>
          <div className="overline">Billing</div>
          <h3 className="font-heading text-2xl uppercase">Subscription</h3>
        </div>
      </div>

      <ul className="text-sm text-zinc-300 space-y-1 mb-4">
        <li>
          Plan: <span className="text-zinc-100">{planLabel(sub.plan)}</span>
          {price && <span className="text-zinc-500"> · {price}</span>}
        </li>
        <li>
          Status:{" "}
          <span
            data-testid="subscription-status"
            className={`uppercase tracking-widest text-xs ${
              isCancelled ? "text-amber-400" : sub.status === "active" || sub.status === "trial" ? "text-blaze" : "text-zinc-400"
            }`}
          >
            {sub.status || "—"}
          </span>
        </li>
        {endDate && (
          <li data-testid="subscription-end-date">
            {isCancelled
              ? <>Access until: <span className="text-zinc-100">{endDate}</span></>
              : sub.one_time
                ? <>Package expires: <span className="text-zinc-100">{endDate}</span></>
                : sub.trial
                  ? <>Trial ends (then billed): <span className="text-zinc-100">{endDate}</span></>
                  : <>Renews: <span className="text-zinc-100">{endDate}</span></>}
          </li>
        )}
      </ul>

      {sub.cancellable && (
        <>
          <button
            type="button"
            data-testid="settings-cancel-subscription-btn"
            onClick={cancel}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 uppercase tracking-widest text-xs rounded-sm transition-colors disabled:opacity-50"
          >
            <XCircle className="h-4 w-4" />
            {busy ? "Cancelling…" : `Cancel ${sub.trial ? "free trial" : "subscription"}`}
          </button>
          <p className="text-[11px] text-zinc-500 mt-3 uppercase tracking-widest">
            Access continues to end of billing period · No refunds
          </p>
        </>
      )}
      {isCancelled && endDate && (
        <p className="text-sm text-amber-400/90" data-testid="subscription-cancelled-note">
          Your subscription is cancelled. You keep full access until {endDate} — no further charges.
        </p>
      )}
      {sub.one_time && !isCancelled && (
        <p className="text-[11px] text-zinc-500 uppercase tracking-widest">
          Prepaid package — never auto-renews, expires naturally
        </p>
      )}
    </div>
  );
};

export default SubscriptionCard;
